import { DownloadOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { ColumnsType } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import 'moment-timezone';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { IPrehledy } from 'types/spravceUctuType';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import { IPrehled } from 'services/akcionar/prehled';
import fileDownload from 'services/fileDownload';
import stahnoutKorporatniAkciApi from 'services/korporatniAkce/stahnoutKorporatniAkci';
import kontrolaXMLApi from 'services/ucastnik/kontrolaXML';
import ucastnikDownload from 'services/ucastnik/ucastnikDownload';
import prehledZpravy, { IPrehledUcastnik } from 'services/ucastnik/zpravy';

import { ETypUdalosti } from 'enums/akcionarKorpAkce/typUdalosti';
import { EkorpAkceButtonType } from 'enums/korporatniAkceTypes';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

interface IModal {
    open: boolean;
    text: string;
    type: EkorpAkceButtonType | null;
}

const defaultModal: IModal = {
    open: false,
    text: '',
    type: null
};
interface IPotvrditFilelist {
    id: string | number;
    typudalost?: ETypUdalosti;
    datumzverejneni?: string;
    idSRD?: number;
}

interface UcastnikPrehledProps {}

export const UcastnikPrehled: React.FC<UcastnikPrehledProps> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { id } = useParams<{ id: string }>();
    const { aktivniFirma, language } = useContext(Context);
    const { setLevel } = useProfily();
    const [data, setData] = useState<IPrehledUcastnik>();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingBtn, setLoadingBtn] = useState<boolean>(false);
    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);
    const [checking, setChecking] = useState<boolean>(false);
    const [uploadingError, setUploadingError] = useState<number>(0);
    const [modal, setModal] = useState<IModal>(defaultModal);
    const [modalFilelist, setModalFilelist] = useState<IFileList[]>([]);
    const [potvrditFilelist, setPotvrditFilelist] = useState<IFileList[]>([]);
    const [modalUploadData, setModalUploadData] = useState<IPotvrditFilelist>();
    const [reload, setReload] = useState<boolean>(false);
    const [tableOffset, setTableOffset] = useState<number>(0);

    useEffect(() => {
        setLoading(true);
        prehledZpravy(tableOffset)
            .then(resp => setData(resp))
            .finally(() => setLoading(false));
    }, [id, reload, tableOffset]);

    const downloadVYPISODP = () => {
        setLoadingBtn(val => !val);
        stahnoutKorporatniAkciApi(id!, ETypUdalosti.VYPISODP)
            .then(resp => fileDownload(resp.data.redirect))
            .finally(() => {
                setLoadingBtn(val => !val);
                setModal(defaultModal);
            });
    };
    const downloadVHODP = () => {
        setLoadingBtn(val => !val);
        stahnoutKorporatniAkciApi(id!, ETypUdalosti.VHODP)
            .then(resp => fileDownload(resp.data.redirect))
            .finally(() => {
                setLoadingBtn(val => !val);
                setModal(defaultModal);
            });
    };

    const uploadFile = (buttonType: string) => {
        kontrolaXMLApi(
            modalUploadData ? potvrditFilelist[0].fileName : modalFilelist[0].fileName,
            modalUploadData ? potvrditFilelist[0].name : modalFilelist[0].name,
            buttonType === EkorpAkceButtonType.SRD_DETAIL_TL_VHODPZRUS ? ETypUdalosti.VHODPZRUS : ETypUdalosti.VHODP,
            modalUploadData && modalUploadData.id
        )
            ?.then(() => {
                setModal(defaultModal);
                setReload(val => !val);
            })
            .catch((err: any) => {
                const errorCode = parseInt(err.response?.data.message.charAt(7));
                setUploadingError(Number.isNaN(errorCode) ? 6 : errorCode);
            })
            .finally(() => {
                setChecking(false);
            });
    };

    const columns: ColumnsType<{
        key: number;
        type: ETypUdalosti;
        tmp: string;
        nazevsouboru: string;
        date: string;
        download: React.ReactNode;
        elhlasovani: boolean;
        eltermin: string;
        idhlasovani: number;
        hlasovaniDetails: IPrehled;
    }> = [
        {
            title: <TranslateText>DATUM</TranslateText>,
            dataIndex: 'date',
            key: 'date'
        },
        {
            title: <TranslateText>TYP</TranslateText>,
            dataIndex: 'type',
            key: 'type',
            render: (type, row) => {
                return (
                    getLanguageObject(language)[`TYPE_${type}`] +
                    (row.elhlasovani
                        ? ' (' +
                          getLanguageObject(language).HLASOVANIE_DO +
                          ' ' +
                          dayjs(row.eltermin).format('DD.MM.YYYY') +
                          ')'
                        : '')
                );
            }
        },
        {
            dataIndex: 'tmp',
            key: 'tmp',
            render: (_, row, index) => (
                <Row>
                    <Col>
                        {row.type === ETypUdalosti.VHODP && (
                            <AnchorComponent
                                id={`ucastnikZrusitBtn${index}`}
                                onClick={() => {
                                    setPotvrditFilelist([]);
                                    setModalUploadData({
                                        id: row.key,
                                        typudalost: row.type,
                                        datumzverejneni: String(row.date),
                                        idSRD: row.key
                                    });
                                    setModal({
                                        open: true,
                                        text: 'SRD_DETAIL_INFO_ZRUS',
                                        type: EkorpAkceButtonType.SRD_DETAIL_TL_VHODPZRUS
                                    });
                                }}
                            >
                                <TranslateText>ZRUSIT</TranslateText>
                            </AnchorComponent>
                        )}
                        {row.elhlasovani && (
                            <AnchorComponent
                                id={`ucastnikHlasovatBtn${index}`}
                                onClick={() => {
                                    setLevel('spravceSelectedCard', {
                                        ...row.hlasovaniDetails,
                                        id: row.idhlasovani,
                                        isUcastnik: true
                                    } as unknown as IPrehledy);
                                    navigateWorkaround(
                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.PREHLED_UCASTNIK_HLASOVANI}`
                                    );
                                    /* setPotvrditFilelist([]);

                                    setModalUploadData({
                                        id: row.key,
                                        typudalost: row.type,
                                        datumzverejneni: String(row.date),
                                        idSRD: row.key
                                    });
                                    setModal({
                                        open: true,
                                        text: 'SRD_DETAIL_INFO_HLAS',
                                        type: EkorpAkceButtonType.SRD_DETAIL_TL_VHODP
                                    }); */
                                }}
                            >
                                <TranslateText>DETAIL</TranslateText>
                            </AnchorComponent>
                        )}
                    </Col>
                </Row>
            )
        },
        {
            title: <TranslateText>STAHNOUT</TranslateText>,
            dataIndex: 'download',
            key: 'download',
            render: download => download,
            align: 'center'
        }
    ];

    return (
        <LoadingWrapper
            loading={loading}
            style={{
                width: '100%'
            }}
        >
            {data ? (
                <>
                    <Row justify="start">
                        <Title text={'PREHLED_ZPRAV'} level={4} />
                    </Row>
                    <TableComponent
                        rowKey={e => e.key}
                        columns={columns}
                        dataSource={data.zadosti.map(akce => ({
                            key: akce.id,
                            elhlasovani: akce.elhlasovani,
                            eltermin: akce.eltermin,
                            idhlasovani: akce.idhlasovani,
                            date: String(dayjs(akce.datumzalozeni).format('DD.MM.YYYY HH:mm:ss')),
                            type: akce.typudalosti,
                            tmp: akce.tmp,
                            nazevsouboru: akce.nazevsouboru,
                            download: (
                                <Row justify="center">
                                    <DownloadOutlined
                                        onClick={() => {
                                            setLoadingModal(val => !val);
                                            ucastnikDownload(akce.tmp)
                                                .then(resp => {
                                                    fileDownload(resp.data.redirect, akce.nazevsouboru);
                                                })
                                                .finally(() => setLoadingModal(val => !val));
                                        }}
                                    />
                                </Row>
                            ),
                            hlasovaniDetails: {
                                id: akce.id,
                                isin: akce.isin,
                                nazevemise: akce.nazevemise,
                                datumkonani: akce.datumkonani,
                                rozhodnyden: akce.rozhodnyden,
                                elhlasovani: akce.elhlasovani,
                                eltermin: akce.eltermin,
                                stavhl: akce.stavhl,
                                typudalosti: akce.typudalosti,
                                predano: akce.predano,
                                potvrzeno: akce.potvrzeno
                            }
                        }))}
                        loading={loadingModal}
                        pagination={{
                            total: data?.count,
                            onChange: page => setTableOffset((page - 1) * 10),
                            current: tableOffset / 10 + 1,
                            showSizeChanger: false
                        }}
                    />
                    <Modal
                        open={modal.open}
                        onCancel={() => {
                            setModal(defaultModal);
                            setModalUploadData(undefined);
                            setUploading(false);
                            setUploadingError(0);
                        }}
                        title={
                            <TranslateText>
                                {modal.type
                                    ? modal.type === 'SRD_DETAIL_TL_VHODP'
                                        ? 'HLASOVANI'
                                        : modal.type
                                    : 'Hlasovať'}
                            </TranslateText>
                        }
                        footer={[
                            <Row
                                className="button-row"
                                style={{
                                    justifyContent: 'flex-end'
                                }}
                            >
                                <ButtonComponent
                                    key={modal.text}
                                    id="ucastnikModalZavritBtn"
                                    buttontext="ZAVRIT"
                                    onClick={() => {
                                        setModal(defaultModal);
                                        setModalUploadData(undefined);
                                        setUploading(false);
                                        setUploadingError(0);
                                    }}
                                />
                                <ButtonComponent
                                    id="ucastnikModalSubmitBtn"
                                    loading={checking || uploading}
                                    buttontext="ODESLAT"
                                    disabled={modalUploadData ? potvrditFilelist.length < 1 : modalFilelist.length < 1}
                                    onClick={() => {
                                        uploadFile(modal.type ? modal.type : '');
                                        setChecking(true);
                                    }}
                                />
                            </Row>
                        ]}
                    >
                        <Row className="button-row" justify="center">
                            {modal.text === EkorpAkceButtonType.SRD_DETAIL_TL_VYPISODP && (
                                <ButtonComponent
                                    buttontext={modal.text}
                                    onClick={downloadVYPISODP}
                                    loading={loadingBtn}
                                    id="ucastnikModalDownloadBtn"
                                />
                            )}
                            {modal.text === EkorpAkceButtonType.SRD_DETAIL_TL_VHODP && (
                                <ButtonComponent
                                    buttontext={modal.text}
                                    onClick={downloadVHODP}
                                    loading={loadingBtn}
                                    id="ucastnikDownloadVHbtn"
                                />
                            )}
                            {![
                                EkorpAkceButtonType.SRD_DETAIL_TL_VYPISODP,
                                EkorpAkceButtonType.SRD_DETAIL_TL_VHODP
                            ].includes(modal.text as EkorpAkceButtonType) && (
                                <Row justify="center">
                                    <Paragraph text={modal.text} />
                                    <Row
                                        style={{
                                            marginTop: '10px'
                                        }}
                                    >
                                        <UploadComponent
                                            accept=".xml"
                                            buttonLoading={uploading}
                                            fileList={modalUploadData ? potvrditFilelist : modalFilelist}
                                            onRemove={() => {
                                                modalUploadData ? setPotvrditFilelist([]) : setModalFilelist([]);
                                                setUploading(false);
                                                setUploadingError(0);
                                            }}
                                            buttonDisabled={
                                                modalUploadData ? potvrditFilelist.length > 0 : modalFilelist.length > 0
                                            }
                                            onChange={uplData => {
                                                uplData.event && setUploading(true);
                                                if (uplData.fileList)
                                                    setPotvrditFilelist(
                                                        uplData.fileList
                                                            .filter(f => f.status && f.status !== 'error')
                                                            .map(e => {
                                                                return {
                                                                    // oid: resp.id!,
                                                                    uid: e.uid!,
                                                                    name: e.name.normalize()!,
                                                                    fileName: e.fileName
                                                                        ? e.fileName
                                                                        : e.response
                                                                          ? e.response.tempnazev
                                                                          : ''!,
                                                                    type: e.type!,
                                                                    size: e.size!,
                                                                    status: e.status ? e.status : 'error'
                                                                };
                                                            })
                                                    );
                                                if (uplData.file.response) {
                                                    setUploading(false);
                                                    uplData.fileList
                                                        .filter(f => f.status && f.status !== 'error')
                                                        .map(e =>
                                                            modalUploadData
                                                                ? setPotvrditFilelist([
                                                                      {
                                                                          uid: e.uid,
                                                                          name: e.name.normalize(),
                                                                          fileName: e.fileName
                                                                              ? e.fileName
                                                                              : e.response
                                                                                ? e.response.tempnazev
                                                                                : '',
                                                                          type: e.type!,
                                                                          size: e.size!,
                                                                          status: e.status ? e.status : 'error'
                                                                      }
                                                                  ])
                                                                : setModalFilelist([
                                                                      {
                                                                          uid: e.uid,
                                                                          name: e.name.normalize(),
                                                                          fileName: e.fileName
                                                                              ? e.fileName
                                                                              : e.response
                                                                                ? e.response.tempnazev
                                                                                : '',
                                                                          type: e.type!,
                                                                          size: e.size!,
                                                                          status: e.status ? e.status : 'error'
                                                                      }
                                                                  ])
                                                        );
                                                }
                                            }}
                                        />
                                    </Row>
                                    {uploadingError > 0 && (
                                        <p className="ant-typography extramessage-text">
                                            <TranslateText>
                                                {uploadingError === 1
                                                    ? 'SRD_XML_ERR_NENALEZENO'
                                                    : uploadingError === 2
                                                      ? 'SRD_XML_ERR_PO_TERMINU'
                                                      : uploadingError === 3
                                                        ? 'SRD_XML_ERR_CHYBNY_UCET'
                                                        : uploadingError === 4
                                                          ? 'SRD_XML_ERR_CHYBNA_OSOBA'
                                                          : uploadingError === 5
                                                            ? 'SRD_XML_ERR_CHYBNE_HLASOVANI'
                                                            : 'NEVALIDNI_XSD_SOUBOR'}
                                            </TranslateText>
                                        </p>
                                    )}
                                </Row>
                            )}
                        </Row>
                    </Modal>
                </>
            ) : null}
        </LoadingWrapper>
    );
};
